import api from './api';
import { 
  Artifact, 
  ArtifactSummary, 
  ArtifactFilter, 
  ArtifactVersion,
  ExportFormat,
  ExportResult,
  ArtifactRelationship,
  ArtifactRelationshipType
} from '../types/artifact';
import { LineageRelationType } from '../types/lineage';

export async function getSessionArtifacts(
  sessionId: string,
  filter?: ArtifactFilter
): Promise<ArtifactSummary[]> {
  const response = await api.get(`/sessions/${sessionId}/artifacts`, {
    params: filter
  });
  return response.data;
}

export async function getAllArtifacts(
  filter?: ArtifactFilter
): Promise<ArtifactSummary[]> {
  const response = await api.get('/artifacts/', {
    params: filter
  });
  return response.data;
}

export interface CreateArtifactRequest {
  name: string;
  type: ArtifactType;
  content: any;
  phase?: string;
  tags?: string[];
  session_id?: string;
  agent_id?: string;
  agent_name?: string;
  metadata?: Record<string, any>;
}

export async function createArtifact(
  artifactData: CreateArtifactRequest
): Promise<Artifact> {
  const response = await api.post('/artifacts', artifactData);
  return response.data;
}

export async function getArtifact(artifactId: string): Promise<Artifact> {
  const response = await api.get(`/artifacts/${artifactId}/`);
  return response.data;
}

export async function getArtifactVersions(
  artifactId: string
): Promise<ArtifactVersion[]> {
  const response = await api.get(`/artifacts/${artifactId}/versions`);
  return response.data;
}

export async function getArtifactVersion(
  artifactId: string,
  versionId: string
): Promise<ArtifactVersion> {
  const response = await api.get(`/artifacts/${artifactId}/versions/${versionId}`);
  return response.data;
}

export async function compareArtifactVersions(
  artifactId: string,
  versionA: string,
  versionB: string,
  format: 'unified' | 'side-by-side' | 'word' = 'unified',
  contextLines: number = 3,
  ignoreWhitespace: boolean = false
): Promise<any> {
  const response = await api.get(`/artifacts/${artifactId}/compare`, {
    params: { 
      version_a: versionA, 
      version_b: versionB,
      format,
      context_lines: contextLines,
      ignore_whitespace: ignoreWhitespace
    }
  });
  return response.data;
}

export async function compareImageArtifacts(
  artifactId: string,
  versionA: string,
  versionB: string
): Promise<any> {
  const response = await api.post(`/artifacts/${artifactId}/compare-images`, null, {
    params: { 
      version_a: versionA, 
      version_b: versionB
    }
  });
  return response.data;
}

export async function getArtifactVersionImageUrl(
  artifactId: string,
  versionId: string
): Promise<string> {
  // This is a simplified approach - in a real app, you'd need to fetch the actual image URL
  // from the version data or have a dedicated endpoint
  const version = await getArtifactVersion(artifactId, versionId);
  
  if (typeof version.content === 'string') {
    if (version.content.startsWith('http') || version.content.startsWith('data:')) {
      return version.content;
    }
  }
  
  if (typeof version.content === 'object' && version.content !== null) {
    if (version.content.url) {
      return version.content.url;
    }
    if (version.content.image) {
      return version.content.image;
    }
  }
  
  return '';
}

export async function restoreArtifactVersion(
  artifactId: string,
  versionId: string
): Promise<Artifact> {
  const response = await api.post(`/artifacts/${artifactId}/restore`, {
    version_id: versionId
  });
  return response.data;
}

export async function exportArtifact(
  artifactId: string,
  format: ExportFormat
): Promise<ExportResult> {
  const response = await api.get(`/artifacts/${artifactId}/export`, {
    params: { format }
  });
  return response.data;
}

export async function searchArtifacts(query: string): Promise<ArtifactSummary[]> {
  const response = await api.get('/artifacts/search/', {
    params: { q: query }
  });
  return response.data;
}

// Relationship management

export async function createArtifactRelationship(
  sourceId: string,
  targetId: string,
  type: ArtifactRelationshipType,
  description?: string,
  strength?: number,
  metadata?: Record<string, any>
): Promise<ArtifactRelationship> {
  const response = await api.post(`/artifacts/${sourceId}/relationships`, {
    source_id: sourceId,
    target_id: targetId,
    type,
    description,
    strength,
    metadata
  });
  return response.data;
}

export async function getArtifactRelationships(
  artifactId: string,
  direction: 'outgoing' | 'incoming' | 'both' = 'outgoing',
  type?: ArtifactRelationshipType
): Promise<ArtifactRelationship[]> {
  const response = await api.get(`/artifacts/${artifactId}/relationships`, {
    params: { direction, type }
  });
  return response.data;
}

export async function deleteArtifactRelationship(
  artifactId: string,
  relationshipId: string
): Promise<void> {
  await api.delete(`/artifacts/${artifactId}/relationships/${relationshipId}`);
}

export async function getArtifactDependencyGraph(
  artifactId: string,
  depth: number = 2,
  includeTypes?: ArtifactRelationshipType[]
): Promise<any> {
  const response = await api.get(`/artifacts/${artifactId}/graph`, {
    params: { 
      depth,
      include_types: includeTypes
    }
  });
  return response.data;
}

// Lineage management

export async function getArtifactLineage(
  artifactId: string,
  maxDepth: number = 3,
  includeTypes?: LineageRelationType[]
): Promise<any> {
  const response = await api.get(`/artifacts/${artifactId}/lineage`, {
    params: { 
      max_depth: maxDepth,
      include_types: includeTypes
    }
  });
  return response.data;
}

export async function getArtifactAncestry(
  artifactId: string,
  maxDepth: number = 3,
  includeTypes?: LineageRelationType[]
): Promise<any> {
  const response = await api.get(`/artifacts/${artifactId}/ancestry`, {
    params: { 
      max_depth: maxDepth,
      include_types: includeTypes
    }
  });
  return response.data;
}

export async function getArtifactDescendants(
  artifactId: string,
  maxDepth: number = 3,
  includeTypes?: LineageRelationType[]
): Promise<any> {
  const response = await api.get(`/artifacts/${artifactId}/descendants`, {
    params: { 
      max_depth: maxDepth,
      include_types: includeTypes
    }
  });
  return response.data;
}

// Function to get all artifacts for workflow canvas
export async function getArtifacts() {
  // Use the existing getAllArtifacts function
  return await getAllArtifacts();
}
